import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, ArrowLeft, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { TokenService } from '@/services/tokenService';
import { supabase } from '@/integrations/supabase/client';

export const ResendVerificationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const initialEmail = useMemo(() => searchParams.get('email') || '', [searchParams]);

  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);

  const sendEmail = async (to: string, token: string) => {
    const verificationLink = `${window.location.origin}/verify-email?token=${token}`;
    const html = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="color: #16a34a; margin: 0;">GreenCard Sustainability Manager</h1>
          <p style="color: #666; margin: 4px 0 0;">Sustainable Business Intelligence Platform</p>
        </div>
        <h2 style="color: #1a1a1a;">Verify your email</h2>
        <p style="color: #444; line-height: 1.6;">
          We noticed you tried to sign in before confirming your account.
          Click the button below to verify your email and activate your account.
        </p>
        <div style="text-align: center; margin: 30px 0;">
          <a href="${verificationLink}"
            style="display: inline-block; padding: 14px 32px; background-color: #16a34a; color: white;
                   text-decoration: none; border-radius: 8px; font-size: 16px; font-weight: bold;">
            Verify Email Address
          </a>
        </div>
        <p style="color: #666; font-size: 14px; line-height: 1.6;">
          This link will expire in <strong>1 hour</strong>.
          If you did not request this, you can ignore this email.
        </p>
        <hr style="border: none; border-top: 1px solid #eee; margin: 30px 0;" />
        <p style="color: #999; font-size: 12px; text-align: center;">
          GreenCard Sustainability Platform · If the button doesn't work, copy this link:<br/>
          <a href="${verificationLink}" style="color: #16a34a;">${verificationLink}</a>
        </p>
      </div>
    `;

    const { data, error } = await supabase.functions.invoke('send-email', {
      body: {
        to,
        subject: 'Verify your GreenCard account',
        html,
      },
    });

    if (error) {
      throw new Error(error.message);
    }

    if (data && !data.success) {
      throw new Error(data.error || 'Failed to send verification email');
    }
  };

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedEmail)) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const { success, token } = await TokenService.createEmailVerificationToken(normalizedEmail);
      if (!success || !token) {
        throw new Error('Could not create a verification link.');
      }

      await sendEmail(normalizedEmail, token);

      toast({
        title: 'Verification Email Sent',
        description: 'A fresh verification link has been sent. Check your inbox.',
      });
    } catch (error) {
      toast({
        title: 'Unable to Send Email',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-4">
          <div className="flex justify-center">
            <div className="bg-green-600 p-3 rounded-full">
              <img src="/greencard-uploads/logo.png" alt="Logo" className="h-8 w-8 bg-white rounded-full object-contain" />
            </div>
          </div>
          <CardTitle className="text-center">Verify Your Email</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert className="border-amber-200 bg-amber-50">
            <Mail className="h-4 w-4 text-amber-700" />
            <AlertDescription className="text-amber-800">
              Your account is currently unverified. Please click on the "Resend verification email" button below to receive a new verification link.
            </AlertDescription>
          </Alert>

          <form onSubmit={handleResend} className="space-y-4">
            <div>
              <Label htmlFor="verification-email">Email address</Label>
              <Input
                id="verification-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Resend verification email'
              )}
            </Button>
          </form>

          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => navigate(`/auth?email=${encodeURIComponent(email.trim())}`)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to sign in
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
